// src/pages/info/EditDBSeries.jsx

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import CancelEditingModal from '../../../components/Modals/CancelEditingModal';
import SaveChangesModal from '../../../components/Modals/SaveChangesModal';
import '../DBSeries/DBSeries.css';
import axios from 'axios';
import Modal from 'react-modal';
import API_BASE_URL from '../../../config';

Modal.setAppElement('#root');

// Парсинг описания на блоки: заголовки **...** и абзацы @@...@@
const parseDescription = raw => {
  const parts = [];
  const regex = /(\*\*)([\s\S]*?)\1|@@([\s\S]*?)@@/g;
  let match;
  while ((match = regex.exec(raw))) {
    if (match[2]) parts.push({ type: 'heading', text: match[2].trim() });
    else if (match[3]) parts.push({ type: 'paragraph', text: match[3].trim() });
  }
  return parts;
};

export default function EditDBSeries() {
  const { seriesId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // Откуда пришли и данные дома (если были переданы)
  const from = location.state?.from;
  const house = location.state?.house;

  // Основные состояния формы и галереи
  const [selectedSeries, setSelectedSeries] = useState(null);
  const [name, setName] = useState('');
  const [descriptionParts, setDescriptionParts] = useState([]);
  const [newImagesFiles, setNewImagesFiles] = useState([]);
  const [newImagePreviews, setNewImagePreviews] = useState([]);
  const [deletedImageIds, setDeletedImageIds] = useState([]);

  // Refs для автоподстройки высоты текстовых полей и для скрытого input[type="file"]
  const partsRef = useRef({});
  const fileInputRef = useRef(null);

  // Для lightbox (превью картинок)
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxSrc, setLightboxSrc] = useState('');

  // Модалки:
  //  - showCancelModal  = при нажатии «Отменить» (CancelEditingModal)
  //  - showSaveModal    = при попытке уйти через Sidebar/«Выход» (SaveChangesModal)
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [pendingPath, setPendingPath] = useState(null);

  // Функция автоподстройки высоты текстового поля
  const autoGrow = el => {
    if (el) {
      el.style.height = 'auto';
      el.style.height = `${el.scrollHeight}px`;
    }
  };

  // Открыть lightbox для картинки
  const openLightbox = url => {
    setLightboxSrc(url);
    setLightboxOpen(true);
  };

 useEffect(() => {
   const onNavigateAttempt = (e) => {
     setPendingPath(e.detail);      // e.detail — либо строка, либо функция onLogout
     setShowSaveModal(true);
   };
   window.addEventListener('navigateAttempt', onNavigateAttempt);
   return () => {
     window.removeEventListener('navigateAttempt', onNavigateAttempt);
   };
 }, []);

// ─── вставить эту функцию в EditDBSeries.jsx ─────────────────────────────────
const handleSaveAndNavigate = async (pathOrFunc) => {
  const success = await saveChanges();
  if (!success) return;
  if (typeof pathOrFunc === 'function') {
    pathOrFunc();
  } else {
    navigate(pathOrFunc, { state: { selectedId: seriesId, from, house } });
  }
};

  // ─── 2. Загрузка данных серии и её картинок ─────────────────────────────────────
  useEffect(() => {
    fetch(`${API_BASE_URL}/building/series_all`)
      .then(res => (res.ok ? res.json() : Promise.reject(res.status)))
      .then(data => {
        const series = data.find(item => item.id === Number(seriesId));
        setSelectedSeries(series);
        setName(series.name);
        setDescriptionParts(parseDescription(series.description));
      })
      .catch(console.error);
  }, [seriesId]);

  // Подстройка высоты всех полей описания при каждом изменении текста
  useEffect(() => {
    Object.values(partsRef.current).forEach(autoGrow);
  }, [descriptionParts]);

  if (!selectedSeries) return null;

  // Обработчики изменения описания
  const handlePartChange = (idx, text) => {
    setDescriptionParts(parts => {
      const next = [...parts];
      next[idx] = { ...next[idx], text };
      return next;
    });
  };

  // «Отменить» (CancelEditingModal): вернуться назад без сохранения
  const handleCancel = () => {
    setShowCancelModal(true);
  };
  const confirmCancel = () => {
    setShowCancelModal(false);
    if (from === 'editHouse' && house) {
      navigate(`/db-house/${house.id}`, { state: house });
    } else {
       navigate('/db-series', { state: { selectedId: seriesId } });
    }
  };

  // Обработчик выбора новых файлов
  const handleAddPhotoClick = () => {
    if (fileInputRef.current) fileInputRef.current.click();
  };
  const handleFileChange = e => {
    const chosenFiles = Array.from(e.target.files);
    const imageFiles = chosenFiles.filter(f => f.type.startsWith('image/'));
    if (imageFiles.length < chosenFiles.length) {
      alert('Можно добавить только изображения');
    }
    if (imageFiles.length === 0) {
      e.target.value = null;
      return;
    }

    setNewImagesFiles(prevFiles => {
      const existingIds = new Set(prevFiles.map(f => `${f.name}_${f.size}`));
      const uniqueFiles = imageFiles.filter(f => {
        const id = `${f.name}_${f.size}`;
        return !existingIds.has(id);
      });
      if (uniqueFiles.length < imageFiles.length) {
        alert('Некоторые файлы уже были добавлены и были проигнорированы');
      }
      return [...prevFiles, ...uniqueFiles];
    });

    setNewImagePreviews(prevPreviews => {
      const existingIds = new Set(newImagesFiles.map(f => `${f.name}_${f.size}`));
      const uniqueFiles = imageFiles.filter(f => {
        const id = `${f.name}_${f.size}`;
        return !existingIds.has(id);
      });
      const newPreviews = uniqueFiles.map(file => URL.createObjectURL(file));
      return [...prevPreviews, ...newPreviews];
    });

    e.target.value = null;
  };

  // Пометка существующей фотографии на удаление
  const handleMarkDeleteExisting = imageId => {
    setDeletedImageIds(prev => [...prev, imageId]);
    setSelectedSeries(prev => ({
      ...prev,
      images: prev.images.filter(img => img.id !== imageId)
    }));
  };
  // Удаление из списка новых фото (еще не сохранённых)
  const handleDeleteNew = idx => {
    setNewImagesFiles(prev => prev.filter((_, i) => i !== idx));
    setNewImagePreviews(prev => prev.filter((_, i) => i !== idx));
  };

  // ─── 3. Сохранение: текст + удаление + загрузка фото ───────────────────────────
  // Возвращает true/false в зависимости от успеха
  const saveChanges = async () => {
    // 1) Склеиваем описание
    const mergedDescription = descriptionParts
      .map(p => (p.type === 'heading' ? `**${p.text}**` : `@@${p.text}@@`))
      .join('');

    // 2) PUT для текста
    const textForm = new URLSearchParams();
    textForm.append('name', name);
    textForm.append('description', mergedDescription);

    try {
      await axios.put(
        `${API_BASE_URL}/building/series/${seriesId}`,
        textForm.toString(),
        { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } }
      );
    } catch (err) {
      console.error('Ошибка при обновлении текста серии:', err);
      alert('Не удалось сохранить изменения текста');
      return false;
    }

    // 3) Удаление помеченных фото
    if (deletedImageIds.length > 0) {
      for (const imageId of deletedImageIds) {
        try {
          await axios.delete(
            `${API_BASE_URL}/building/series/${seriesId}/images/${imageId}`
          );
        } catch (err) {
          console.error(`Ошибка при удалении фотографии ${imageId}:`, err);
        }
      }
    }

    // 4) Загрузка новых фото
    if (newImagesFiles.length > 0) {
      try {
        const filesForm = new FormData();
        newImagesFiles.forEach(file => {
          filesForm.append('images', file);
        });
        await axios.post(
          `${API_BASE_URL}/building/add_series/${seriesId}/images`,
          filesForm,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );
      } catch (err) {
        console.error('Ошибка при загрузке новых фотографий:', err);
        alert('Не удалось загрузить новые фотографии');
        return false;
      }
    }

    // 5) Обновляем локально список фото
    try {
      const resp = await axios.get(
        `${API_BASE_URL}/building/get_image/${seriesId}/series_by_id_series`
      );
      setSelectedSeries(prev => ({ ...prev, images: resp.data }));
      setNewImagesFiles([]);
      setNewImagePreviews([]);
      setDeletedImageIds([]);
    } catch (err) {
      console.error('Ошибка при получении обновленных изображений:', err);
    }

    return true;
  };

 const handleSave = async () => {
   const success = await saveChanges();
   if (!success) return;

   // Если пришли со страницы редактирования дома, просто возвращаемся назад
   if (from === 'editHouse' && house) {
     navigate(-1);
   } else {
     // Иначе — возвращаемся на список серий и выделяем отредактированную
     navigate('/db-series', { state: { selectedId: seriesId } });
   }
 };

  return (
    <div className="check-page series_page">
      {/* ─── Заголовок: стрелка назад, кнопки Отменить/Сохранить ──────────────────── */}
      <div className="edit-header-home header_series">
        <button className="back-btn-home" onClick={handleCancel}>
          <span className="back-icon-home">⌄</span>
        </button>
        <h1 className="page-title series_title">
          Редактирование серии: {selectedSeries.name}
        </h1>
        <div className="edit-actions">
          <button className="primary-btn cancel" onClick={handleCancel}>
            Отменить
          </button>
          <button className="primary-btn save" onClick={handleSave}>
            Сохранить
          </button>
        </div>
      </div>

      {/* ─── Модалка «Отменить без сохранения» ───────────────────────────────────── */}
      {showCancelModal && (
        <CancelEditingModal
          onClose={() => {
            setShowCancelModal(false);
          }}
          onConfirm={confirmCancel}
        />
      )}

      {/* ─── Основная часть: форма и галерея ───────────────────────────────────────── */}
      <div className="edit-house-body">
        {/* Левая часть: поля текста */}
        <div className="edit-house-left">
          <div className="edit-house-table-container">
            <div className="edit-house-table-wrapper">
              <table className="edit-house-table">
                <tbody>
                  <tr className="edit-house-table__row">
                    <td className="edit-house-table__cell edit-house-table__cell--label">
                      Серия дома
                    </td>
                    <td className="edit-house-table__cell edit-house-table__cell--value">
                      <input
                        type="text"
                        className="edit-series-input"
                        value={name}
                        onChange={e => setName(e.target.value)}
                      />
                    </td>
                  </tr>
                  <tr className="edit-house-table__row edit-house-desc-row">
                    <td className="edit-house-table__cell edit-house-table__cell--label">
                      Описание серии
                    </td>
                    <td className="edit-house-table__cell edit-house-table__cell--desc edit-house-desc-cell">
                      <div className="description-parts">
                        {descriptionParts.map((part, idx) => (
                          <div className="description-part" key={idx}>
                            <label className="description-label">
                              {part.type === 'heading' ? 'Заголовок' : 'Абзац'}
                            </label>
                            {part.type === 'heading' ? (
                              <input
                                ref={el => (partsRef.current[idx] = el)}
                                className="description-input"
                                type="text"
                                value={part.text}
                                onChange={e =>
                                  handlePartChange(idx, e.target.value)
                                }
                                onInput={e => autoGrow(e.target)}
                              />
                            ) : (
                              <textarea
                                ref={el => (partsRef.current[idx] = el)}
                                className="description-textarea"
                                value={part.text}
                                onChange={e =>
                                  handlePartChange(idx, e.target.value)
                                }
                                onInput={e => autoGrow(e.target)}
                              />
                            )}
                          </div>
                        ))}
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className="edit-divider" />

        {/* Правая часть: галерея */}
        <div className="edit-house-right nh">
          <input
            type="file"
            accept="image/*"
            multiple
            ref={fileInputRef}
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />

          <div className="collector-photo-gallery nh">
            <div className="collector-photo-item ssss">
              <button
                className="collector-add-photo-btn primary-btn-save nh"
                onClick={handleAddPhotoClick}
              >
                Добавить чертёж +
              </button>
            </div>

            {/* Превью новых фотографий */}
            {newImagePreviews.map((src, i) => (
              <div key={`new-${i}`} className="collector-photo-item nh">
                <span
                  className="collector-photo-close nh"
                  onClick={() => handleDeleteNew(i)}
                >
                  &times;
                </span>
                <img
                  src={src}
                  alt={`Новый чертёж ${i + 1}`}
                  className="clickable-image"
                  onClick={() => openLightbox(src)}
                />
              </div>
            ))}

            {/* Существующие фотографии */}
            {selectedSeries.images?.length > 0 &&
              selectedSeries.images.map((img, i) => (
                <div key={img.id} className="collector-photo-item nh">
                  <span
                    className="collector-photo-close nh"
                    onClick={() => handleMarkDeleteExisting(img.id)}
                  >
                    ×
                  </span>
                  <img
                    src={img.url}
                    alt={`Чертёж ${i + 1}`}
                    className="clickable-image"
                    onClick={() => openLightbox(img.url)}
                  />
                </div>
              ))}

            <Modal
              isOpen={lightboxOpen}
              onRequestClose={() => setLightboxOpen(false)}
              className="lightbox-modal"
              overlayClassName="lightbox-overlay"
            >
              <img
                src={lightboxSrc}
                alt="Чертёж во весь экран"
                className="lightbox-image"
                onClick={() => setLightboxOpen(false)}
              />
            </Modal>
          </div>
        </div>
      </div>

      {/* ─── 4. Модалка «Сохранить изменения?» при попытке навигации/выхода ───────── */}
     {showSaveModal && pendingPath && (
       <SaveChangesModal
         onClose={() => {
           setShowSaveModal(false);
           setPendingPath(null);
         }}
         onConfirm={async () => {
           setShowSaveModal(false);
           if (pendingPath) {
             await handleSaveAndNavigate(pendingPath);
             setPendingPath(null);
           }
         }}
         onCancel={() => {
           setShowSaveModal(false);
           setPendingPath(null);
         }}
       />
     )}
    </div>
  );
}
